export default class Minimap
{
    constructor(_options)
    {
        this.time = _options.time
        this.car = _options.car
        this.physics = _options.physics

        this.bounds = {
            minX: - 50,
            maxX: 44,
            minY: - 70,
            maxY: 16
        }

        this.places = [
            { label: 'Start', x: 0, y: 2, anchor: 'north' },
            { label: 'Projects', x: 30, y: - 30, anchor: 'west' },
            { label: 'Contact', x: 2, y: - 58, anchor: 'east' },
            { label: 'Work', x: - 22, y: - 54, anchor: 'west' },
            { label: 'Play', x: - 40, y: - 34, anchor: 'north' }
        ]

        this.roads = [
            { ax: 0, ay: 8, bx: 0, by: - 32, width: 7.2 },
            { ax: - 2, ay: - 30, bx: 36, by: - 30, width: 6.4 },
            { ax: 0, ay: - 30, bx: 2, by: - 64, width: 5.6 },
            { ax: - 2, ay: - 32, bx: - 24, by: - 56, width: 5.4 },
            { ax: - 2, ay: - 32, bx: - 44, by: - 34, width: 5.4 }
        ]

        this.size = 320
        this.setElement()
        this.drawMap()

        this.time.on('tick', () =>
        {
            this.update()
        })
    }

    setElement()
    {
        this.$element = document.createElement('div')
        this.$element.className = 'minimap'
        this.$element.setAttribute('aria-hidden', 'true')

        this.$canvas = document.createElement('canvas')
        this.$canvas.className = 'minimap-canvas'
        this.$canvas.width = this.size
        this.$canvas.height = this.size
        this.$element.appendChild(this.$canvas)

        this.context = this.$canvas.getContext('2d')

        this.$north = document.createElement('span')
        this.$north.className = 'minimap-north'
        this.$north.textContent = 'N'
        this.$element.appendChild(this.$north)

        document.body.appendChild(this.$element)

        const spanX = this.bounds.maxX - this.bounds.minX
        const spanY = this.bounds.maxY - this.bounds.minY
        const pad = 36
        const inner = this.size - pad * 2
        this.scale = inner / Math.max(spanX, spanY)
        this.offsetX = pad + (inner - spanX * this.scale) / 2
        this.offsetY = pad + (inner - spanY * this.scale) / 2
    }

    project(_x, _y)
    {
        return {
            x: this.offsetX + (_x - this.bounds.minX) * this.scale,
            y: this.size - (this.offsetY + (_y - this.bounds.minY) * this.scale)
        }
    }

    drawMap()
    {
        const map = document.createElement('canvas')
        map.width = this.size
        map.height = this.size
        const context = map.getContext('2d')
        const center = this.size / 2
        const radius = this.size / 2 - 8

        context.clearRect(0, 0, this.size, this.size)
        context.save()
        context.beginPath()
        context.arc(center, center, radius, 0, Math.PI * 2)
        context.clip()

        const ground = context.createRadialGradient(center, center * 0.85, radius * 0.15, center, center, radius)
        ground.addColorStop(0, '#f6b27a')
        ground.addColorStop(1, '#e08b52')
        context.fillStyle = ground
        context.fillRect(0, 0, this.size, this.size)

        context.lineCap = 'round'
        context.lineJoin = 'round'
        context.strokeStyle = '#fff6ec'
        context.globalAlpha = 0.92

        for(const road of this.roads)
        {
            const from = this.project(road.ax, road.ay)
            const to = this.project(road.bx, road.by)
            context.lineWidth = road.width * this.scale
            context.beginPath()
            context.moveTo(from.x, from.y)
            context.lineTo(to.x, to.y)
            context.stroke()
        }

        context.globalAlpha = 1

        for(const place of this.places)
        {
            const point = this.project(place.x, place.y)

            context.fillStyle = '#fffaf4'
            context.beginPath()
            context.arc(point.x, point.y, 5.5, 0, Math.PI * 2)
            context.fill()

            let labelX = point.x
            let labelY = point.y - 16
            context.textAlign = 'center'

            if(place.anchor === 'west')
            {
                labelX = point.x - 10
                labelY = point.y
                context.textAlign = 'right'
            }
            else if(place.anchor === 'east')
            {
                labelX = point.x + 10
                labelY = point.y
                context.textAlign = 'left'
            }

            context.font = '700 16px "Comic Neue", sans-serif'
            context.textBaseline = 'middle'
            context.lineWidth = 5
            context.strokeStyle = 'rgba(90, 52, 28, 0.35)'
            context.strokeText(place.label, labelX, labelY)
            context.fillStyle = '#fffaf4'
            context.fillText(place.label, labelX, labelY)
        }

        const shade = context.createRadialGradient(center, center, radius * 0.55, center, center, radius)
        shade.addColorStop(0, 'rgba(0, 0, 0, 0)')
        shade.addColorStop(1, 'rgba(120, 52, 18, 0.18)')
        context.fillStyle = shade
        context.fillRect(0, 0, this.size, this.size)

        context.restore()

        context.beginPath()
        context.arc(center, center, radius, 0, Math.PI * 2)
        context.lineWidth = 8
        context.strokeStyle = '#fffaf4'
        context.stroke()

        this.map = map
    }

    show()
    {
        this.$element.classList.add('is-visible')
    }

    update()
    {
        if(!this.$element.classList.contains('is-visible') || !this.car || !this.physics.car)
        {
            return
        }

        const position = this.car.position
        const angle = this.physics.car.angle || 0
        const point = this.project(position.x, position.y)
        const context = this.context
        const center = this.size / 2
        const radius = this.size / 2 - 8

        context.clearRect(0, 0, this.size, this.size)
        context.drawImage(this.map, 0, 0)

        context.save()
        context.beginPath()
        context.arc(center, center, radius - 2, 0, Math.PI * 2)
        context.clip()

        context.translate(point.x, point.y)
        context.rotate(- angle)

        context.fillStyle = 'rgba(255, 250, 244, 0.28)'
        context.beginPath()
        context.moveTo(0, 0)
        context.arc(0, 0, 34, - 0.55, 0.55)
        context.closePath()
        context.fill()

        const pulse = 7 + Math.sin(this.time.elapsed * 0.004) * 1.4
        context.strokeStyle = 'rgba(255, 250, 244, 0.7)'
        context.lineWidth = 2
        context.beginPath()
        context.arc(0, 0, pulse, 0, Math.PI * 2)
        context.stroke()

        context.fillStyle = '#e36b4f'
        context.strokeStyle = '#fffaf4'
        context.lineWidth = 2.5
        context.lineJoin = 'round'
        context.beginPath()
        context.moveTo(11, 0)
        context.lineTo(- 7, 6.5)
        context.lineTo(- 3.5, 0)
        context.lineTo(- 7, - 6.5)
        context.closePath()
        context.fill()
        context.stroke()

        context.restore()
    }
}
